import {
  ProcedureRequestStatus,
  type FullProcedureDefinitionDTO,
  type ProcedureDocumentDTO,
  type FullProcedureRequestDTO,
  type ProcedureTimeDTO,
} from "@wkt/api/procedures";
import type { SimpleDate } from "@wkt/api/employee-calendar/employeeCalendarDTO";

export type ProcedureRequestDialogMode =
  | "create"
  | "edit"
  | "approve"
  | "read-only";

export interface ProcedureRequestDialogOpenContext {
  mode: ProcedureRequestDialogMode;
  editable: boolean;
  asApprover: boolean;
  deleteOnClose: boolean;
  requestId?: number | null;
  definitionId?: number | null;
}

export interface ProcedureRequestFieldVisibility {
  showFromDate: boolean;
  showToDate: boolean;
  showFromTime: boolean;
  showToTime: boolean;
  showQuantity: boolean;
  showFreeText: boolean;
  showVacationDays: boolean;
  showProject: boolean;
  showActivityType: boolean;
  allowUploadDocument: boolean;
  enableAbsenceCategory: boolean;
}

export interface ProcedureRequestDialogFieldEditability {
  requestTitle: boolean;
  fromDate: boolean;
  toDate: boolean;
  fromTime: boolean;
  toTime: boolean;
  freeText: boolean;
  quantity: boolean;
  project: boolean;
  activityType: boolean;
  hoursBag: boolean;
  absenceCategory: boolean;
  attachments: boolean;
  approverComment: boolean;
  confirmAction: boolean;
  deleteAction: boolean;
}

export interface ProcedureRequestDialogSubmitPayload {
  requestTitle: string;
  requestInfo: string;
  fromDate: SimpleDate | null;
  toDate: SimpleDate | null;
  fromHour: ProcedureTimeDTO | null;
  toHour: ProcedureTimeDTO | null;
  quantity: number | null;
  projectId: number | null;
  activityTypeId: number | null;
  hoursBagType: number | null;
  hoursBagDelta: number | null;
  paidAbsenceCategoryId: number | null;
  approverComment: string;
}

export interface ProcedureRequestDialogDraftState {
  requestTitle: string;
  requestInfo: string;
  fromDate: SimpleDate | null;
  toDate: SimpleDate | null;
  fromTimeText: string;
  toTimeText: string;
  vacationDaysText: string;
  quantity: string;
  projectId: number | null;
  activityTypeId: number | null;
  hoursBagType: number | null;
  paidAbsenceCategoryId: number | null;
  approverComment: string;
  documents?: ProcedureDocumentDTO[];
}

export function resolveProcedureRequestDialogMode(
  request?: Pick<FullProcedureRequestDTO, "id" | "requestStatus" | "expenseSheetId"> | null,
  options: { asApprover?: boolean; editable?: boolean } = {}
): ProcedureRequestDialogMode {
  const { asApprover = false, editable = false } = options;

  if (asApprover) {
    return editable ? "approve" : "read-only";
  }

  if (!request?.id || request.requestStatus === ProcedureRequestStatus.PREPARING) {
    return "create";
  }

  if (!editable) {
    return "read-only";
  }

  return "edit";
}

export function resolveProcedureRequestDialogOpenContext(
  request?: Pick<
    FullProcedureRequestDTO,
    "id" | "requestStatus" | "expenseSheetId" | "requestType"
  > | null,
  options: {
    mode?: ProcedureRequestDialogMode;
    asApprover?: boolean;
    editable?: boolean;
    deleteOnClose?: boolean;
  } = {}
): ProcedureRequestDialogOpenContext {
  const mode =
    options.mode ??
    resolveProcedureRequestDialogMode(request, {
      asApprover: options.asApprover,
      editable: options.editable,
    });

  return {
    mode,
    editable: options.editable ?? mode !== "read-only",
    asApprover: options.asApprover ?? mode === "approve",
    deleteOnClose: options.deleteOnClose ?? false,
    requestId: request?.id ?? null,
    definitionId: request?.requestType ?? null,
  };
}

export function resolveProcedureRequestFieldVisibility(
  definition?: FullProcedureDefinitionDTO | null
): ProcedureRequestFieldVisibility {
  return {
    showFromDate: Boolean(definition?.showFromDate),
    showToDate: Boolean(definition?.showToDate),
    showFromTime: Boolean(definition?.showFromTime),
    showToTime: Boolean(definition?.showToTime),
    showQuantity: Boolean(definition?.showQuantity),
    showFreeText: Boolean(definition?.showFreeText),
    showVacationDays: Boolean(definition?.showVacationDays),
    showProject: Boolean(definition?.showProject),
    showActivityType: Boolean(definition?.showActivityType),
    allowUploadDocument: Boolean(definition?.allowUploadDocument),
    enableAbsenceCategory: Boolean(definition?.enableAbsenceCategory),
  };
}

export function resolveProcedureRequestFieldEditability(
  context: ProcedureRequestDialogOpenContext,
  request?: Pick<FullProcedureRequestDTO, "expenseSheetId" | "projectId"> | null
): ProcedureRequestDialogFieldEditability {
  const isApprove = context.mode === "approve";
  const isMutable = context.mode === "create" || context.mode === "edit";
  const expenseSheetLinked = request?.expenseSheetId != null;
  const hasLockedProject = request?.projectId != null && context.requestId != null;

  return {
    requestTitle: isMutable && !expenseSheetLinked,
    fromDate: isMutable && !expenseSheetLinked,
    toDate: isMutable,
    fromTime: isMutable,
    toTime: isMutable,
    freeText: isMutable,
    quantity: isMutable && !expenseSheetLinked,
    project: isMutable && !expenseSheetLinked && !hasLockedProject,
    activityType: isMutable,
    hoursBag: isMutable,
    absenceCategory: isMutable,
    attachments: isMutable && !isApprove,
    approverComment: isApprove,
    confirmAction: context.mode !== "read-only",
    deleteAction: isProcedureRequestDeleteActionVisible(context) && !expenseSheetLinked,
  };
}

export function isProcedureRequestDeleteActionVisible(
  context: ProcedureRequestDialogOpenContext
): boolean {
  return (
    context.mode === "edit" &&
    !context.deleteOnClose &&
    context.requestId != null
  );
}
